/**
 * @description
 * Analytics tabs component for switching between domain and user analytics views.
 * Provides a clean interface to toggle between different analytics perspectives.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/AnalyticsTabs.test.tsx
 */

import { BarChart3, Users } from 'lucide-react';
import type { AnalyticsViewMode } from '../../../types/vscode-lens';

interface AnalyticsTabsProps {
  currentMode: AnalyticsViewMode;
  onModeChange: (mode: AnalyticsViewMode) => void;
}

/**
 * Analytics tabs component
 */
export function AnalyticsTabs({ currentMode, onModeChange }: AnalyticsTabsProps) {
  const tabs = [
    {
      id: 'domain' as AnalyticsViewMode,
      label: 'Domain Analytics',
      icon: BarChart3,
      description: 'Analyze data by email domains',
    },
    {
      id: 'user' as AnalyticsViewMode,
      label: 'User Analytics',
      icon: Users,
      description: 'Analyze data by individual users',
    },
  ];

  return (
    <div className="rounded-lg border bg-card p-1">
      <div className="flex gap-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = currentMode === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => onModeChange(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              }`}
              title={tab.description}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
